import { Component, Prop, Vue } from 'vue-property-decorator';
import i18n from '../../lang';
import { TranslateResult } from 'vue-i18n';
import { MultiLanguage } from '@/Domain/Entities';

@Component
export default class SdMultiSelect extends Vue {
    @Prop() value!: any[];
    @Prop() label?: string;
    @Prop() options!: any | any[];
    @Prop({ default: false }) required!: boolean;
    @Prop() placeholder?: string;
    @Prop({ default: false }) disabled!: boolean;
    @Prop({ default: false }) submitted!: boolean;
    @Prop({ default: true }) filter!: boolean;
    @Prop({ default: 3 }) maxSelectedLabels!: number;
    @Prop() selectedItemsLabel?: string;
    @Prop() currentLanguage?: string;
    @Prop({ default: false }) useTags!: boolean;
    @Prop() tooltipText?: string;

    dirty: boolean = false;
    isOpen: boolean = false;
    focused: boolean = false;
    dataReady: boolean = false;
    filterText: string = '';
    optionalText: TranslateResult | string = '';

    get isRequired() {
        return this.required && 
            (!Array.isArray(this.value) || this.value.length === 0) && 
            (this.dirty || this.submitted);
    }

    get optionsToManage() {
        if (!this.options) {
            return [];
        }
        if (Array.isArray(this.options)) {
            if (this.options.length === 0) {
                return [];
            }
            const firstElement = this.options[0];
            if (typeof firstElement === 'object' && firstElement !== null) {
                if (firstElement.name instanceof MultiLanguage) {
                    const lang = this.currentLanguage || 'es-ES';
                    return this.options.map((element: any) => ({
                        name: element.name.getValue(lang),
                        id: element.id,
                        title: element.name.getValue(lang),
                        disabled: element.disabled || false
                    }));
                }
                return this.options.map((element: any) => ({
                    name: i18n.t(element.name) as string,
                    id: element.id,
                    title: (i18n.t(element.title) || i18n.t(element.name)) as string,
                    disabled: element.disabled || false
                }));
            }
            return this.options.map((element: any) => ({
                name: String(element),
                id: element,
                title: String(element),
                disabled: false
            }));
        }

        const optionsToManage: any[] = [];
        for (const element in this.options) {
            if (Object.prototype.hasOwnProperty.call(this.options, element)) {
                optionsToManage.push({
                    name: i18n.t(this.options[element]) as string,
                    id: element,
                    title: i18n.t(this.options[element]) as string,
                    disabled: this.options[element].disabled
                });
            }
        }
        return optionsToManage.sort((a, b) => a.name.localeCompare(b.name));
    }

    get filteredOptions() {
        if (!this.filterText) {
            return this.optionsToManage;
        }
        const query = this.filterText.toLowerCase().trim();
        return this.optionsToManage.filter((option: any) => 
            option.name.toLowerCase().includes(query)
        );
    }

    get selectedOptions() {
        return this.optionsToManage.filter((option: any) => Array.from(this.value).includes(option.id));
    }

    get visibleSelectedOptions() {
        return this.selectedOptions.slice(0, this.maxSelectedLabels);
    }

    get remainingSelectedCount() {
        return Math.max(0, this.selectedOptions.length - this.maxSelectedLabels);
    }

    get displayText() {
        if (this.selectedOptions.length === 0) {
            return this.placeholderText;
        }
        const template = this.selectedOptions.map((opt: any) => opt.name);

        if (this.selectedOptions.length <= this.maxSelectedLabels) {
            return template.join(', ');
        }
        return `${template.slice(0, this.maxSelectedLabels).join(', ')}... +${this.selectedOptions.length - this.maxSelectedLabels}`;
    }

    get isAllSelected() {
        const enabledOptions = this.optionsToManage.filter((opt: any) => !opt.disabled);
        if (enabledOptions.length === 0) { return false; }
        const values = Array.isArray(this.value) ? this.value : [];
        return enabledOptions.every((opt: any) => values.includes(opt.id));
    }

    get placeholderText(): string {
        return this.placeholder || (i18n.t('lang.shared.select') as string) || 'Seleccionar';
    }

    mounted() {
        this.$nextTick(() => {
            this.dataReady = true;
        });
        this.setOptionalText();
        document.addEventListener('click', this.handleClickOutside);
    }

    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    }

    toggleDropdown() {
        if (this.disabled) { return; }
        this.isOpen = !this.isOpen;
        this.focused = this.isOpen;
        if (this.isOpen) {
            this.$emit('focus');
            this.$nextTick(() => {
                const searchInput = this.$refs.filterInput as HTMLInputElement;
                if (searchInput) {
                    searchInput.focus();
                }
            });
        } else {
            this.dirty = true;
            this.$emit('blur');
        }
    }

    handleClickOutside(event: MouseEvent) {
        const container = this.$refs.container as HTMLElement;
        const overlay = this.$refs.overlay as HTMLElement;
        if (
            container && !container.contains(event.target as Node) &&
            (!overlay || !overlay.contains(event.target as Node))
        ) {
            if (this.isOpen) {
                this.isOpen = false;
                this.focused = false;
                this.dirty = true;
                this.$emit('blur');
            }
        }
    }

    isSelected(option: any): boolean {
        const values = Array.isArray(this.value) ? this.value : [];
        return values.includes(option.id);
    }

    toggleOption(option: any) {
        if (option.disabled || this.disabled) { return; }
        const currentValue = Array.isArray(this.value) ? [...this.value] : [];
        const index = currentValue.indexOf(option.id);
        if (index > -1) {
            currentValue.splice(index, 1);
        } else {
            currentValue.push(option.id);
        }
        this.$emit('input', currentValue);
        this.$emit('change', currentValue);
    }

    toggleSelectAll() {
        if (this.disabled) { return; }
        const enabledOptions = this.optionsToManage.filter((opt: any) => !opt.disabled);
        const values = Array.isArray(this.value) ? [...this.value] : [];
        
        const enabledIds = enabledOptions.map((opt: any) => opt.id);
        const allEnabledSelected = enabledIds.every(id => values.includes(id));

        let newValue: any[];
        if (allEnabledSelected) {
            newValue = values.filter(val => !enabledIds.includes(val));
        } else {
            newValue = Array.from(new Set([...values, ...enabledIds]));
        }

        this.$emit('input', newValue);
        this.$emit('change', newValue);
    }

    private setOptionalText(): void {
        this.optionalText = !this.required && this.label !== undefined ? i18n.t('lang.input.optional') : '';
    }
}
